import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Copy, ExternalLink, Eye } from "lucide-react";

import { Button } from "../../components/common/Button";
import { Input } from "../../components/common/Input";
import { Modal } from "../../components/common/Modal";
import { revealDeviceCredentials } from "../../api/settings";
import type { DeviceCredentials } from "../../api/settings";
import { formatApiError } from "../../lib/apiError";
import type { Device } from "../../types";

interface Props {
  device: Device | null;
  onClose: () => void;
}

function CopyField({ label, value, href }: { label: string; value: string; href?: string }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copiado`);
    } catch {
      toast.error("No se pudo copiar al portapapeles");
    }
  };
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted w-24 shrink-0">{label}</span>
      {!value ? (
        <span className="flex-1 text-sm text-muted italic">(no disponible)</span>
      ) : href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 inline-flex items-center gap-1 font-mono text-sm text-accent hover:underline truncate"
          title="Abrir la interfaz web del dispositivo en una pestaña nueva"
        >
          {value}
          <ExternalLink size={12} className="shrink-0" />
        </a>
      ) : (
        <span className="flex-1 font-mono text-sm text-text truncate select-all">{value}</span>
      )}
      {value && (
        <Button variant="ghost" size="sm" onClick={copy} title={`Copiar ${label.toLowerCase()}`}>
          <Copy size={12} />
        </Button>
      )}
    </div>
  );
}

export function RevealCredentialsModal({ device, onClose }: Props) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [creds, setCreds] = useState<DeviceCredentials | null>(null);

  // El usuario pidio que la contrasena se pida cada vez: reseteamos todo al
  // abrir/cerrar el modal (cambia `device`).
  useEffect(() => {
    setPassword("");
    setError("");
    setCreds(null);
    setSubmitting(false);
  }, [device]);

  if (!device) return null;

  const submit = async () => {
    if (submitting || !password) return;
    setSubmitting(true);
    setError("");
    try {
      setCreds(await revealDeviceCredentials(device.id, password));
    } catch (e) {
      setError(formatApiError(e, "No se pudieron obtener los datos"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={`Ver datos — ${device.name}`} width="md">
      {!creds ? (
        <div className="space-y-3">
          <p className="text-xs text-muted">
            Ingresá la contraseña de "Ver datos" para mostrar el usuario, la
            contraseña y la IP de{" "}
            <span className="text-text font-medium">{device.name}</span>.
          </p>
          <Input
            label="Contraseña"
            type="password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); if (error) setError(""); }}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            autoFocus
            autoComplete="off"
            error={error || undefined}
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancelar</Button>
            <Button variant="primary" onClick={submit} loading={submitting} disabled={!password}>
              <Eye size={14} /> Ver datos
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="space-y-2 border border-line rounded p-3 bg-bg">
            <CopyField label="Usuario" value={creds.username} />
            <CopyField label="Contraseña" value={creds.password} />
            <CopyField label="Dirección IP" value={creds.host} href={creds.web_url} />
          </div>
          <p className="text-xs text-muted">
            Hacé clic en la IP para abrir la interfaz web del dispositivo en una
            pestaña nueva. Usá el botón de copiar para llevar cada dato al portapapeles.
          </p>
          <div className="flex justify-end">
            <Button variant="primary" onClick={onClose}>Cerrar</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
